import { ReactElement, useMemo } from 'react';

import { useDialogState } from '../../../../../../../../../common/hooks/useDialogState';
import { IndicationStatus } from '../../../../../../../../../graphql';
import { useIndicationActivitySyndicateContext } from '../../../../../context/IndicationActivitySyndicateContext';
import { type Props as ReadButtonProps } from '../../../common/indication-section-read-view/ReadButtons';
import { useDeleteInstitutionalIndication } from '../../hooks/useDeleteInstitutionalIndication';
import CancelIndicationConfirmDialog from '../cancel-indication-confirm-dialog/CancelIndicationConfirmDialog';
import DeleteIndicationConfirmDialog from '../delete-indication-confirm-dialog/DeleteIndicationConfirmDialog';
import { useUpdateIndicationStatus } from './useUpdateIndicationStatus';

export type Params = Readonly<{
  isInvestorCovered: boolean;
}>;

export const useCreateSyndicateDuplicateReadButtonProps = ({
  isInvestorCovered,
}: Params): ReadButtonProps & { duplicateReadButtonDialogs: ReactElement[] } => {
  const {
    offeringId,
    access: { canUpdateIndication },
    basicPublishedOffering,
  } = useIndicationActivitySyndicateContext();

  const indication = basicPublishedOffering!.orderBook.institutionalIndication!;
  const investorName = indication.investorInformation.bankInvestorName ?? '';

  const {
    isOpen: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDialogState();
  const {
    isOpen: isCancelDialogOpen,
    onOpen: openCancelDialog,
    onClose: closeCancelDialog,
  } = useDialogState();

  const [onUpdateStatus, isUpdatingStatus] = useUpdateIndicationStatus(offeringId, indication.id);

  const [onDelete, isDeleting] = useDeleteInstitutionalIndication(indication.id, investorName);

  return useMemo(
    () => ({
      baseButtonAccess: canUpdateIndication && isInvestorCovered,
      isVisible: {
        edit: false,
        pass: false,
        reactivate: indication.status === IndicationStatus.Cancelled && isInvestorCovered,
        cancel: indication.status !== IndicationStatus.Cancelled && isInvestorCovered,
        delete: isInvestorCovered,
      },
      menuItemProps: {
        reactivate: {
          disabled: isUpdatingStatus,
          onClick: () => onUpdateStatus(IndicationStatus.Active),
        },
        cancel: {
          onClick: openCancelDialog,
        },
        delete: {
          disabled: isDeleting,
          onClick: openDeleteDialog,
        },
      },
      duplicateReadButtonDialogs: [
        <CancelIndicationConfirmDialog
          indicationId={indication.id}
          offeringId={offeringId}
          onClose={closeCancelDialog}
          isOpen={isCancelDialogOpen}
        />,
        <DeleteIndicationConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
          isDeleting={isDeleting}
          onDelete={onDelete}
          investorName={investorName}
        />,
      ],
    }),
    [
      indication.status,
      canUpdateIndication,
      closeCancelDialog,
      closeDeleteDialog,
      indication.id,
      investorName,
      isInvestorCovered,
      isCancelDialogOpen,
      isDeleteDialogOpen,
      isDeleting,
      isUpdatingStatus,
      offeringId,
      onDelete,
      onUpdateStatus,
      openCancelDialog,
      openDeleteDialog,
    ]
  );
};
